/**
 * App Router
 * Handles authentication, organization management, and switches between simulation platforms
 */

import { BrowserRouter, Routes, Route, Navigate, useNavigate, Outlet } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Container,
  Icon,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
} from '@chakra-ui/react'
import { ArrowBackIcon, ChevronDownIcon, CheckCircleIcon } from '@chakra-ui/icons'
import { MdAutoAwesome, MdLogout, MdPerson, MdSettings, MdBusiness } from 'react-icons/md'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import Login from './pages/Login'
import LandingPage from './pages/LandingPage'
import AuthCallback from './pages/AuthCallback'
import OrganizationInvitationCallback from './pages/OrganizationInvitationCallback'
import App from './App'
import { V3App } from './v3'
import { V4App } from './v4'
import { V5App, V5Routes } from './v5'
import { NavigationBlockerModal } from './v4/components/NavigationBlockerModal'
import { trackVersionSwitch, trackPageView, trackButtonClick } from './analytics'
import UserbackWidget from './components/UserbackWidget'
import {
  OrganizationProvider,
  OrganizationSwitcher,
  RequireOrganization,
  OrganizationSettingsPage,
} from './components/organization'

function OrganizationSelectPage() {
  const { currentOrg } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (currentOrg) {
      navigate('/app')
    }
  }, [currentOrg, navigate])

  return (
    <RequireOrganization>
      <Box minH="100vh" bg="gray.50" />
    </RequireOrganization>
  )
}

function HomePage() {
  const [version, setVersion] = useState<'v2' | 'v3' | 'v4' | 'v5' | null>('v5')
  const [hasActiveSimulation, setHasActiveSimulation] = useState(false)
  const [showNavigationModal, setShowNavigationModal] = useState(false)
  const { user, signOut, isAuthEnabled, currentOrg, canManageOrg } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (version) {
      trackPageView(`${version}_app`)
    } else {
      trackPageView('homepage')
    }
  }, [version])

  const handleVersionSelect = (newVersion: 'v2' | 'v3' | 'v4' | 'v5') => {
    trackVersionSwitch(version, newVersion)
    setVersion(newVersion)
  }

  const handleBackToHome = () => {
    if (hasActiveSimulation) {
      setShowNavigationModal(true)
      return
    }
    
    trackButtonClick('back_to_home', version || 'unknown')
    setHasActiveSimulation(false)
    setVersion(null)
  }

  const handleConfirmLeave = () => {
    setShowNavigationModal(false)
    trackButtonClick('back_to_home', version || 'unknown')
    setHasActiveSimulation(false)
    setVersion(null)
  }

  const handleCancelLeave = () => {
    setShowNavigationModal(false)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const handleOrgSettings = () => {
    navigate('/app/settings')
  }

  const BackButton = () => {
    const [isHovered, setIsHovered] = useState(false)
    
    return (
      <Button
        onClick={handleBackToHome}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        bg="brand.500"
        color="white"
        size="md"
        borderRadius="xl"
        minW={isHovered ? "180px" : "48px"}
        w={isHovered ? "auto" : "48px"}
        h="48px"
        p={0}
        transition="all 0.3s ease-in-out"
        overflow="hidden"
        _hover={{
          bg: "brand.600",
          boxShadow: "glow",
        }}
        _active={{
          bg: "brand.700",
        }}
        justifyContent={isHovered ? "flex-start" : "center"}
      >
        <HStack spacing={0} px={isHovered ? 4 : 0}>
          <ArrowBackIcon boxSize={5} />
          <Box
            opacity={isHovered ? 1 : 0}
            maxW={isHovered ? "200px" : "0px"}
            overflow="hidden"
            transition="all 0.3s ease-in-out"
            whiteSpace="nowrap"
            ml={isHovered ? 2 : 0}
          >
            Back to homepage
          </Box>
        </HStack>
      </Button>
    )
  }

  if (version === 'v2') {
    return (
      <>
        <Box position="fixed" top={4} left={4} zIndex={1000}>
          <BackButton />
        </Box>
        <App />
      </>
    )
  }

  if (version === 'v3') {
    return (
      <>
        <Box position="fixed" top={4} left={4} zIndex={1000}>
          <BackButton />
        </Box>
        <V3App />
      </>
    )
  }

  if (version === 'v4') {
    return (
      <>
        <Box position="fixed" top={4} left={4} zIndex={1000}>
          <BackButton />
        </Box>
        <V4App onSimulationStateChange={setHasActiveSimulation} />
        <NavigationBlockerModal
          isOpen={showNavigationModal}
          onStay={handleCancelLeave}
          onLeave={handleConfirmLeave}
        />
      </>
    )
  }

  if (version === 'v5') {
    return (
      <>
        <V5App />
      </>
    )
  }

  return (
    <>
      <Flex h="100vh" w="100vw" direction="column" bg="background.light">
        {isAuthEnabled && (
          <Flex justify="space-between" align="center" p={4}>
            <OrganizationSwitcher />
            
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                variant="ghost"
                _hover={{ bg: 'gray.100' }}
              >
                <HStack spacing={2}>
                  <Avatar 
                    size="sm" 
                    name={user?.fullName || user?.email || 'User'} 
                    src={user?.imageUrl || undefined} 
                  />
                  <VStack spacing={0} align="start" display={{ base: 'none', md: 'flex' }}>
                    <Text fontSize="sm" color="text.primary" fontWeight="medium">
                      {user?.fullName || user?.email || 'User'}
                    </Text>
                    {currentOrg && (
                      <Text fontSize="xs" color="text.secondary">
                        {currentOrg.name}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </MenuButton>
              <MenuList>
                <MenuItem icon={<Icon as={MdPerson} />} isDisabled>
                  {user?.email}
                </MenuItem>
                {currentOrg && (
                  <MenuItem icon={<Icon as={MdBusiness} />} isDisabled>
                    {currentOrg.name} ({currentOrg.membersCount}/{currentOrg.maxMembers})
                  </MenuItem>
                )}
                <MenuDivider />
                {canManageOrg && (
                  <MenuItem icon={<Icon as={MdSettings} />} onClick={handleOrgSettings}>
                    Organization Settings
                  </MenuItem>
                )}
                <MenuItem icon={<Icon as={MdLogout} />} onClick={handleSignOut}>
                  Sign Out
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        )}
        
        {!isAuthEnabled && (
          <Flex justify="center" p={2} bg="orange.100" borderBottom="1px solid" borderColor="orange.300">
            <Text fontSize="sm" color="orange.800" fontWeight="medium">
              Running in Development Mode - Authentication Disabled
            </Text>
          </Flex>
        )}

        <Flex flex="1" align="center" justify="center" overflowY="auto" overflowX="hidden" pt={8}>
          <Container maxW="container.lg" py={10}>
            <VStack spacing={8}>
              <Box pt={6}>
                <img 
                  src="/axone_logo.png" 
                  alt="Axone Logo" 
                  style={{ 
                    height: '120px', 
                    width: 'auto',
                    marginBottom: '0'
                  }} 
                />
              </Box>
              <VStack spacing={2} style={{ marginTop: '-2rem' }}>
                <Heading size="2xl" fontWeight="700" letterSpacing="-0.02em" color="text.primary">
                  Simulation Platform
                </Heading>
                <Text fontSize="lg" color="text.secondary">
                  Test your skills in dynamic, AI-driven simulations
                </Text>
              </VStack>

              <HStack spacing={4} align="stretch" wrap="wrap" justify="center">
                <Card
                  w="520px"
                  cursor="pointer"
                  _hover={{ transform: 'translateY(-4px)', shadow: 'deep' }}
                  transition="all 0.3s"
                  onClick={() => handleVersionSelect('v4')}
                  borderColor="brand.500"
                  borderWidth="2px"
                  bg="white"
                  borderRadius="xl"
                  boxShadow="mid"
                >
                  <CardBody>
                    <VStack align="start" spacing={6}>
                      <Box w="full">
                        <Heading 
                          size="md" 
                          fontWeight="400" 
                          letterSpacing="0.01em" 
                          color="text.primary"
                          lineHeight="1.4"
                          fontFamily="body"
                          mb={3}
                        >
                          The latest evolution of our AI-powered role simulations
                        </Heading>
                        
                        <Text fontSize="md" color="text.secondary" lineHeight="1.7" mb={4}>
                          Step into realistic, role-based simulations designed to evaluate how you think, decide, and respond in real-time. Every scenario adapts to your choices offering a truly personalized evaluation journey.
                        </Text>
                        
                        <VStack align="start" spacing={2}>
                          <HStack spacing={2}>
                            <CheckCircleIcon color="brand.500" />
                            <Text fontSize="sm" color="text.secondary">Adaptive storylines that react to your choices</Text>
                          </HStack>
                          <HStack spacing={2}>
                            <CheckCircleIcon color="brand.500" />
                            <Text fontSize="sm" color="text.secondary">Real-time performance insights</Text>
                          </HStack>
                          <HStack spacing={2}>
                            <CheckCircleIcon color="brand.500" />
                            <Text fontSize="sm" color="text.secondary">Intelligent virtual agents that challenge your decisions</Text>
                          </HStack>
                          <HStack spacing={2}>
                            <CheckCircleIcon color="brand.500" />
                            <Text fontSize="sm" color="text.secondary">Immersive environments that mirror real work situations</Text>
                          </HStack>
                        </VStack>
                      </Box>

                      <Button 
                        variant="solid"
                        w="full"
                        size="lg"
                        borderRadius="md"
                        leftIcon={<Icon as={MdAutoAwesome} />}
                        color="text.primary"
                      >
                        Enter Simulation
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              </HStack>
            </VStack>
          </Container>
        </Flex>
      </Flex>

      <UserbackWidget />
    </>
  )
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrganizationProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            <Route
              path="/organization/select"
              element={
                <ProtectedRoute>
                  <OrganizationSelectPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/organization/settings"
              element={<Navigate to="/app/settings" replace />}
            />
            
            <Route
              path="/organization/invitation-callback"
              element={<OrganizationInvitationCallback />}
            />
            
            <Route
              path="/app/*"
              element={
                <ProtectedRoute>
                  <RequireOrganization>
                    <V5Routes />
                  </RequireOrganization>
                </ProtectedRoute>
              }
            />
          </Routes>
        </OrganizationProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
